class OrganaxCartPreview extends HTMLElement {
  connectedCallback() {
    this.listeners = new AbortController();
    this.trigger = this.querySelector('#cart-icon-bubble');
    this.panel = this.querySelector('.organax-cart-preview__panel');
    this.closeButtons = this.querySelectorAll('[data-cart-close]');
    this.actionButton = this.querySelector('[data-cart-action]');
    this.empty = this.querySelector('.organax-cart-preview__empty');
    this.filled = this.querySelector('.organax-cart-preview__filled');
    this.addedMessage = this.querySelector('.organax-cart-preview__added');
    this.itemsList = this.querySelector('.organax-cart-preview__items');
    this.countLabel = this.querySelector('[data-cart-count]');
    this.subtotal = this.querySelector('[data-cart-subtotal]');
    this.status = this.querySelector('[data-cart-status]');
    this.content = this.querySelector('[data-cart-content]');
    this.body = this.querySelector('.organax-cart-preview__body');
    this.total = this.querySelector('[data-cart-total]');
    this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onTriggerClick = this.handleTriggerClick.bind(this);
    this.trigger.addEventListener('click', this.onTriggerClick, { signal: this.listeners.signal });
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(true), { signal: this.listeners.signal }));
    this.actionButton.addEventListener('click', () => this.loading || this.failed || this.hasItems ? window.location.assign(this.trigger.href) : this.close(true), { signal: this.listeners.signal });
    this.itemsList.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-item-key]');
      if (remove) this.removeItem(remove);
    }, { signal: this.listeners.signal });
    document.addEventListener('pointerdown', this.onDocumentPointerDown, { signal: this.listeners.signal });
    document.addEventListener('keydown', this.onKeyDown, { signal: this.listeners.signal });
  }

  disconnectedCallback() {
    this.cancelRefresh();
    this.listeners?.abort();
  }

  async getCart(signal) {
    const response = await fetch(`${this.trigger.href}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)}`;
    }
  }

  renderCart(cart, added = false) {
    this.hasRenderedCart = true;
    this.failed = false;
    this.hasItems = cart.item_count > 0;
    this.panel.classList.toggle('has-items', this.hasItems);
    this.empty.hidden = this.hasItems;
    this.filled.hidden = !this.hasItems;
    this.addedMessage.hidden = !added || !this.hasItems;
    this.countLabel.textContent = `Subtotal (${cart.item_count} item${cart.item_count === 1 ? '' : 's'})`;
    this.subtotal.textContent = this.formatMoney(cart.total_price, cart.currency);
    this.actionButton.textContent = this.hasItems ? 'Continue to cart' : 'Keep Shopping';
    this.actionButton.classList.toggle('has-items', this.hasItems);
    this.itemsList.replaceChildren();

    for (const item of cart.items || []) {
      const row = document.createElement('li');
      row.className = 'organax-cart-preview__item';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.product_title || '';
        image.width = 56;
        image.height = 56;
        image.loading = 'lazy';
        row.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'organax-cart-preview__item-media';
        row.append(placeholder);
      }
      const details = document.createElement('div');
      const link = document.createElement('a');
      link.href = item.url || this.trigger.href;
      link.textContent = item.product_title || item.title;
      details.append(link);
      const meta = document.createElement('p');
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `${item.variant_title} · ` : '';
      meta.textContent = `${variant}Qty ${item.quantity}`;
      details.append(meta);
      if (item.selling_plan_allocation?.selling_plan?.name) {
        const plan = document.createElement('p');
        plan.textContent = item.selling_plan_allocation.selling_plan.name;
        details.append(plan);
      }
      row.append(details);
      const price = document.createElement('strong');
      price.textContent = this.formatMoney(item.final_line_price, cart.currency);
      row.append(price);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'organax-cart-preview__item-remove';
      remove.setAttribute('aria-label', `Remove ${item.product_title || item.title} from cart`);
      remove.dataset.cartItemKey = item.key;
      remove.textContent = '×';
      row.append(remove);
      this.itemsList.append(row);
    }
  }

  async refreshAndOpen(added = false) {
    this.cancelRefresh();
    const version = this.requestVersion;
    this.requestController = new AbortController();
    this.loading = true;
    this.failed = false;
    this.trigger.setAttribute('aria-busy', 'true');
    this.content.setAttribute('aria-busy', 'true');
    this.status.textContent = this.hasRenderedCart ? 'Updating your cart…' : 'Loading your cart…';
    this.status.hidden = false;
    this.body.classList.toggle('is-pending', !this.hasRenderedCart);
    this.empty.hidden = !this.hasRenderedCart || this.hasItems;
    this.filled.hidden = !this.hasRenderedCart || !this.hasItems;
    this.addedMessage.hidden = true;
    this.total.hidden = !this.hasRenderedCart;
    this.actionButton.textContent = 'Continue to cart';
    this.open(added);
    const controller = this.requestController;
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const cart = await this.getCart(controller.signal);
      if (!this.isConnected || version !== this.requestVersion) return;
      this.renderCart(cart, added);
      this.status.hidden = true;
      this.total.hidden = false;
      return true;
    } catch {
      if (!this.isConnected || version !== this.requestVersion) return;
      this.failed = true;
      this.status.textContent = 'Could not update your cart. Continue to cart to view your items.';
      this.status.hidden = false;
      return false;
    } finally {
      clearTimeout(timeout);
      if (version === this.requestVersion) {
        this.loading = false;
        this.trigger.removeAttribute('aria-busy');
        this.content.removeAttribute('aria-busy');
        this.body.classList.toggle('is-pending', !this.hasRenderedCart);
      }
    }
  }

  cancelRefresh() {
    this.requestVersion = (this.requestVersion || 0) + 1;
    this.requestController?.abort();
    this.loading = false;
    this.trigger?.removeAttribute('aria-busy');
    this.content?.removeAttribute('aria-busy');
  }

  async removeItem(button) {
    if (button.disabled) return;
    this.cancelRefresh();
    this.status.hidden = true;
    button.disabled = true;
    try {
      const response = await fetch(`${this.trigger.href}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: button.dataset.cartItemKey, quantity: 0 }),
      });
      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      const cart = await response.json();
      this.renderCart(cart);
      const bubble = this.trigger.querySelector('.cart-count-bubble');
      if (bubble) {
        bubble.hidden = cart.item_count === 0;
        bubble.querySelectorAll('span').forEach((span, index) => {
          span.textContent = index === 0 ? cart.item_count : `${cart.item_count} items`;
        });
      }
    } catch {
      button.disabled = false;
      button.setAttribute('aria-label', 'Could not remove item. Try again.');
    }
  }

  handleTriggerClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (!this.panel.hidden || this.loading) return this.close(true);
    this.refreshAndOpen();
  }

  open(preservePanels = false) {
    if (!preservePanels) {
      document.querySelector('organax-search[open]')?.close?.();
      document.querySelector('.organax-nav__root[open]')?.removeAttribute('open');
    }
    this.panel.hidden = false;
    // Let the header controller reveal and retain the open cart, including after an AJAX add.
    this.closest('.section-header')?.querySelector('organax-navigation')?.updateHeaderOnScroll();
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close(restoreFocus = false) {
    this.cancelRefresh();
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.trigger.focus();
  }

  handleDocumentPointerDown(event) {
    if ((!this.panel.hidden || this.loading) && !this.contains(event.target)) this.close();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && (!this.panel.hidden || this.loading)) {
      event.preventDefault();
      this.close(true);
    }
  }
}

customElements.define('organax-cart-preview', OrganaxCartPreview);
